import { Card, CardContent, Stack, Typography } from '@mui/material';
import { BarChart, BarChartProps, mangoFusionPalette } from '@mui/x-charts-pro';

export type IncBarChartProps = {
  series: BarChartProps['series'];
  xlabels?: string[];
  title: string;
  BarChartProps?: Partial<BarChartProps>;
  categoryGapRatio?: number;
  'data-testid'?: string;
};

/**
 * Compact y-axis tick formatter: large counts (e.g. 300,000) render as "300K"
 * on the axis so they stay legible, while tooltips keep the full value. Applied
 * as the default y-axis; consumers can override via `BarChartProps.yAxis`.
 */
const compactYAxis: BarChartProps['yAxis'] = [
  {
    valueFormatter: (value, context) => {
      if (value == null) return '';
      if (context.location !== 'tick') return value.toLocaleString();
      return typeof value === 'number'
        ? Intl.NumberFormat('en-US', {
            notation: 'compact',
            maximumFractionDigits: 1,
          }).format(value)
        : String(value);
    },
  },
];

const IncBarChart = ({
  series = [],
  xlabels = [],
  title,
  BarChartProps: barChartProps = {},
  categoryGapRatio = 0.5,
  'data-testid': dataTestId,
}: IncBarChartProps) => {
  return (
    <Card>
      <CardContent
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          '&:last-child': { paddingBottom: 2 },
        }}
      >
        <Typography variant="body2" component="div" margin={0}>
          {title}
        </Typography>
        <Stack flexGrow={1} justifyContent="center" minHeight={0}>
          <BarChart
            data-testid={dataTestId}
            margin={{ bottom: 8 }}
            series={series}
            xAxis={[{ scaleType: 'band', data: xlabels, categoryGapRatio }]}
            yAxis={compactYAxis}
            colors={mangoFusionPalette}
            {...barChartProps}
            slotProps={{
              legend: {
                position: {
                  horizontal: 'center',
                  vertical: 'bottom',
                },
              },
              ...barChartProps.slotProps,
            }}
          />
        </Stack>
      </CardContent>
    </Card>
  );
};

export default IncBarChart;
